import { useCallback, useEffect, useState } from 'react';
import {
  endSupportLogging,
  startSupportLogging,
  subscribeSupportLogging,
} from '../../services/admin/supportLoggingSession';
import {
  readSupportLoggingSession,
  SupportLoggingSession,
} from '../../services/supportLoggingStore';

export function formatTimeLeft(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}

/**
 * An admin's own "Capture my logs" window, including the time left: behind Settings -> Support
 * and the header indicator shown on every admin page while it runs.
 */
export function useSupportLogging() {
  const [session, setSession] = useState<SupportLoggingSession | null>(() =>
    readSupportLoggingSession(),
  );
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => subscribeSupportLogging(setSession), []);

  useEffect(() => {
    if (!session) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [session]);

  const setEnabled = useCallback(async (enabled: boolean) => {
    setBusy(true);
    setError(null);
    try {
      if (enabled) await startSupportLogging();
      else await endSupportLogging();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change log capture');
    } finally {
      setBusy(false);
    }
  }, []);

  return {
    active: session != null,
    secondsLeft: session ? Math.max(0, Math.round((session.expiresAt - now) / 1000)) : 0,
    busy,
    error,
    setEnabled,
  };
}
